import * as React from "react";
import {
	Autocomplete, Backdrop, Box, Button, Card, CardContent, CardHeader, CircularProgress, Container,
	createFilterOptions, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton,
	Paper, Slide, Stack, TextField, Typography, useMediaQuery, useTheme, Snackbar, Alert
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DeleteIcon from "@mui/icons-material/Delete";
import LayersIcon from "@mui/icons-material/Layers";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import PackageIcon from "@mui/icons-material/AllInbox";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import {ThemeProvider} from "@mui/material/styles";
import {useNavigate, useLocation} from "react-router-dom";
import {
	useGetSubCategoriesQuery,
	useAddSubCategoryMutation,
	useDeleteCatalogEntityMutation,
} from "../../store/catalogApi";
import {getSessionToken, getUserName} from "../../utils/authSession";
import {subCategoryList} from "../DepartmentList";
import {buttonTheme} from "../../theme";
import { config } from "../../config";

/* ───────────────── palette  ───────────────── */
const violet = {main: "#7c3aed", light: "#ede9fe", dark: "#5b21b6"};
const indigo = {main: "#4338ca", light: "#eef2ff", dark: "#3730a3"};
const green = {main: "#059669", light: "#d1fae5", dark: "#047857"};
const filter = createFilterOptions();
const Transition = React.forwardRef(function Transition(p, r) {
	return <Slide direction="up" ref={r} {...p} />;
});

/* ═════════ component ═════════ */
export default function ManageSubCategory() {
	const theme = useTheme();
	const isXs = useMediaQuery(theme.breakpoints.down("sm"));

	const navigate = useNavigate();
	const location = useLocation();
	const sessionToken = getSessionToken;

	const catData = location.state || null;
	const {data: posts = [], isLoading, isFetching, isError} = useGetSubCategoriesQuery(
		{vendorId: catData?.vendorId, parentId: catData?.id},
		{skip: !catData}
	);
	const [addSubCategory, {isLoading: adding}] = useAddSubCategoryMutation();
	const [deleteEntity] = useDeleteCatalogEntityMutation();
	const loading = isLoading || isFetching || adding;
	const [value, setValue] = React.useState(null);
	const [dialogOpen, setDialogOpen] = React.useState(false);

	const [confirmOpen, setConfirmOpen] = React.useState(false);
	const [subToDelete, setSubToDelete] = React.useState(null);
	const [deleteLoading, setDeleteLoading] = React.useState(false);

	const [snack, setSnack] = React.useState({
		open: false,
		msg: "",
		severity: "success",
	});
	const handleSnackClose = () => setSnack((s) => ({...s, open: false}));

	const handleDeleteClick = (sub) => {
		setSubToDelete(sub);
		setConfirmOpen(true);
	};

	const confirmDelete = async () => {
		if (!subToDelete) return;
		setDeleteLoading(true);

		try {
			const data = await deleteEntity({
				entityType: "subcategory",
				vendorId: subToDelete.vendorId,
				id: subToDelete.id,
			}).unwrap();

			if (data.canDelete) {
				setSnack({open: true, msg: "Sub‑category deleted.", severity: "success"});
			} else {
				setSnack({
					open: true,
					msg: "Cannot delete. There are products under this sub‑category.",
					severity: "error",
				});
			}
		} catch (e) {
			setSnack({open: true, msg: "Network error. Try again.", severity: "error"});
		}

		setDeleteLoading(false);
		setConfirmOpen(false);
		setSubToDelete(null);
	};

	React.useEffect(() => {
		if (isError) {
			setSnack({open: true, msg: "Could not load sub-categories.", severity: "error"});
		}
	}, [isError]);

	React.useEffect(() => {
		localStorage.setItem("visitedSsc", "true");
		localStorage.setItem("visitedSc", "true");
		// stale caches from the old reducer-based flow
		localStorage.removeItem("SubCategoryData");
	}, []);

	React.useEffect(() => {
		if (!catData) navigate("/sc");
	}, [catData, navigate]);

	/* -------------- handlers -------------- */
	const handleAdd = async () => {
		if (!value?.subCategory) return;
		if (
			posts.some(
				(p) =>
					p.subCategoryName?.toLowerCase() === value.subCategory.toLowerCase()
			)
		) {
			alert("Sub-Category already added");
			return;
		}
		try {
			await addSubCategory({
				subCategoryName: value.subCategory,
				catId: catData.id,
				vendorId: catData.vendorId,
			}).unwrap();
			setValue(null);
			setDialogOpen(false);
		} catch (err) {
			setSnack({open: true, msg: "Could not add sub-category. Please try again.", severity: "error"});
		}
	};

	const handleAddProduct = (s) => {
		const {vendorId} = catData;
		fetch(
			`${config.API_MSWSSTOREMANAGER_BASE_URL}/v2/addNewProduct`,
			{
				method: "POST",
				body: JSON.stringify({
					subCategory: s.subCategoryName,
					venderLoggedUserName: getUserName(),
					vendorId,
					department: catData.departmentName,
					category: catData.categoryName,
				}),
				headers: {
					"Content-Type": "application/json",
					"X-Session-Token": sessionToken()
				},
			}
		)
			.then((r) => r.json())
			.then((d) =>
				navigate(`/ap/${s.subCategoryName}`, {
					state: {
						subCategory: s.subCategoryName,
						productID: d.product.id,
						pricingID: d.pricing.id,
						inventoryID: d.inventory.id,
						additionalInfoID: d.additionInfo.id,
						fulfillmentID: d.fulfilment.id,
						imagesID: d.images.id,
						venderLoggerUserName: getUserName(),
						vendorId,
						department: catData.departmentName,
						category: catData.categoryName,
					},
				})
			);
	};

	/* -------------- early loader -------------- */
	if (!catData)
		return (
			<Backdrop open sx={{color: "#fff", zIndex: theme.zIndex.drawer + 1}}>
				<CircularProgress color="inherit"/>
			</Backdrop>
		);

	const {departmentName, categoryName, vendorId, deptId} = catData;
	const trunc = (str) => (isXs && str.length > 8 ? `${str.slice(0, 8)}…` : str);

	/* -------------- UI -------------- */
	return (
		<Box sx={{bgcolor: indigo.light, minHeight: "100vh", py: {xs: 2, md: 4}}}>
			<ThemeProvider theme={theme}>
				<Container maxWidth="md">
					{/* Header */}
					<Box
						sx={{
							mb: {xs: 2, md: 4},
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							flexWrap: "wrap",
							rowGap: 1,
						}}
					>
						<Button
							startIcon={<ArrowBackIosNewIcon/>}
							variant="outlined" color="secondary"
							sx={{
								borderRadius: 2,
								textTransform: "none",
								fontWeight: 600,
								px: 2.5,
							}}
							onClick={() =>
								navigate(location.state?.fromManageProducts ? "/mp" : "/sc", {
									state: {id: deptId, vendorId, departmentName},
								})
							}
						>
							Back
						</Button>

						{/* breadcrumb */}
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								mx: "auto",
								fontSize: {xs: 12, sm: 14},
								color: indigo.main,
								flexWrap: "wrap",
								justifyContent: "center",
							}}
						>
							{isXs ? (
								// Mobile: only show "Sub-Categories"
								<Typography
									component="span"
									sx={{
										fontWeight: 700,
										fontSize: "1rem",
									}}
								>
									Sub-Categories
								</Typography>
							) : (
								// Larger screens: full breadcrumb
								<>
									<LayersIcon sx={{fontSize: 16}}/>
									<Typography component="span">{trunc(departmentName)}</Typography>
									<ArrowForwardIosIcon sx={{fontSize: 10}}/>
									<Typography component="span">{trunc(categoryName)}</Typography>
									<ArrowForwardIosIcon sx={{fontSize: 10}}/>
									<Typography component="span" fontWeight={600}>
										Sub-Categories
									</Typography>
								</>
							)}
						</Box>
						<Button
							variant="contained"
							color="primary"
							sx={{
								color: '#000',
								borderRadius: 2,
								textTransform: "none",
								fontWeight: 600,
								px: 3,
							}}
							onClick={() =>
								navigate(location.state?.fromManageProducts ? "/mp" : "/home")
							}
						>
							Done
						</Button>
						{/* Context card */}
						<Card
							sx={{
								mt: isXs ? 2 : 3, mb: isXs ? 2 : 3, width: '100%',
								color: "#fff", border: "none", borderRadius: 3,
								backgroundImage: "linear-gradient(90deg,#312e81 0%,#5b21b6 100%)",
								boxShadow: "0 12px 24px rgba(0,0,0,0.25)"
							}}
						>
							<CardContent
								sx={{
									p: {xs: 3, md: 4}, display: "flex", flexDirection: {xs: "column", sm: "row"},
									alignItems: {sm: "center"}, justifyContent: "space-between", gap: 3
								}}
							>
								<Box>
									<Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
										<FolderOpenIcon fontSize="small" sx={{opacity: 0.8}}/>
										<Typography variant="body2" sx={{opacity: 0.8}}>
											{departmentName} → {categoryName}
										</Typography>
									</Box>
									<Typography variant="h6" fontWeight={700} mb={0.5}>
										{categoryName}
									</Typography>
									<Typography variant="body2" sx={{opacity: 0.8}}>
										Add products to sub-categories within this category
									</Typography>
								</Box>

								<Button
									startIcon={<AddIcon/>}
									fullWidth={isXs}
									variant="contained"
									color="primary"
									sx={{
										color: "#000",
										borderRadius: 2,
										textTransform: "none", fontWeight: 600
									}}
									onClick={() => setDialogOpen(true)}
								>
									Add Sub-Category
								</Button>
							</CardContent>
						</Card>

						{/* List */}
						<Card sx={{borderRadius: 3, width: '100%'}}>
							<CardHeader
								title={<Typography fontWeight={700}>Sub-Categories</Typography>}
								subheader={
									<Typography variant="body2" color="text.secondary">
										{posts.length} {posts.length === 1 ? "sub-category" : "sub-categories"} in {categoryName}
									</Typography>
								}
								action={
									<Box
										sx={{
											bgcolor: indigo.light, color: indigo.dark,
											px: 1.5, py: 0.5, borderRadius: 2, fontSize: 14,
											fontWeight: 600
										}}
									>
										{posts.length}
									</Box>
								}
								sx={{pb: 0}}
							/>
							<CardContent sx={{pt: 0}}>
								<Stack spacing={3}>
									{posts.map((s) => (
										<Paper
											key={s.id ?? s.subCategoryName}
											variant="outlined"
											sx={{
												p: 3, borderRadius: 3, borderColor: indigo.light, transition: "all .15s",
												"&:hover": {borderColor: indigo.dark, bgcolor: indigo.light}
											}}
										>
											<Box
												sx={{
													display: "flex", flexDirection: {xs: "column", sm: "row"},
													alignItems: {sm: "center"}, justifyContent: "space-between", gap: 2
												}}
											>
												<Box sx={{display: "flex", alignItems: "center", gap: 2}}>
													<Box
														sx={{
															width: 48,
															height: 48,
															borderRadius: 3,
															bgcolor: violet.light,
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
														}}
													>
														<PackageIcon sx={{color: violet.main}}/>
													</Box>
													<Box>
														<Typography fontWeight={600}>{s.subCategoryName}</Typography>
														<Typography variant="body2" color="text.secondary">
															Ready for product management
														</Typography>
													</Box>
												</Box>

												<Box sx={{display: "flex", alignItems: "center", gap: 2, width: {xs: "100%", sm: "auto"}}}>
													<Button
														fullWidth={isXs}
														startIcon={<ShoppingCartIcon fontSize="small"/>}
														startIcon={<ShoppingCartIcon fontSize="small"/>}
														color="primary"
														variant="contained"
														sx={{
															borderRadius: 2,
															textTransform: "none",
															fontWeight: 600,
															color: "#000"
														}}
														onClick={() => handleAddProduct(s)}
													>
														Add Product
													</Button>
													<IconButton
														size="small"
														onClick={() => handleDeleteClick(s)}
														sx={{color: "#ef4444", "&:hover": {bgcolor: "#fee2e2"}}}
													>
														<DeleteIcon fontSize="small"/>
													</IconButton>
												</Box>
											</Box>
										</Paper>
									))}
								</Stack>
								<Box
									sx={{
										mt: 6,
										p: {xs: 3, sm: 4},
										textAlign: "center",
										borderRadius: 3,
										border: "1px solid",
										borderColor: violet.light,
										backgroundImage: "linear-gradient(135deg,#f6f5ff 0%,#eef2ff 100%)",
									}}
								>
									<ShoppingCartIcon
										sx={{fontSize: {xs: 32, sm: 40}, color: violet.main, mb: 1.5}}
									/>

									<Typography
										variant="subtitle1"
										fontWeight={600}
										mb={1}
										sx={{color: "text.primary"}}
									>
										Ready to add products?
									</Typography>

									<Typography
										variant="body2"
										color="text.secondary"
										mb={2}
										sx={{maxWidth: 420, mx: "auto"}}
									>
										Click “Add Product” on any sub-category above to start adding your
										inventory.
									</Typography>

									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: 1,
											fontSize: 12,
											color: violet.main,
										}}
									>
										<Box
											sx={{
												width: 6,
												height: 6,
												bgcolor: violet.main,
												borderRadius: "50%",
											}}
										/>
										<Typography component="span" sx={{color: violet.main}}>
											Each sub-category can contain multiple products
										</Typography>
									</Box>
								</Box>
							</CardContent>
						</Card>
					</Box>
				</Container>
				<Dialog
					open={confirmOpen}
					onClose={() => setConfirmOpen(false)}
					TransitionComponent={Transition}
					fullWidth
					maxWidth="xs"
					PaperProps={{sx: {borderRadius: 3}}}
				>
					<DialogTitle fontWeight={700}>Delete Sub‑Category?</DialogTitle>

					<DialogContent dividers sx={{pt: 1.5}}>
						<DialogContentText>
							Deleting <strong>{subToDelete?.subCategoryName}</strong> will remove it
							permanently if it contains no products. This action cannot be undone.
							Continue?
						</DialogContentText>
					</DialogContent>

					<DialogActions sx={{px: 3, py: 2}}>
						<Button color='secondary' onClick={() => setConfirmOpen(false)}>Cancel</Button>
						<Button
							variant="contained"
							onClick={confirmDelete}
							disabled={deleteLoading}
							onClick={confirmDelete}
							disabled={deleteLoading}
							color="error"
							sx={{
								textTransform: "none",
								fontWeight: 600,
							}}
						>
							{deleteLoading ? <CircularProgress size={20}/> : "Delete"}
						</Button>
					</DialogActions>
				</Dialog>

				{/* Dialog (unchanged) */}
				<Dialog
					open={dialogOpen}
					onClose={() => setDialogOpen(false)}
					TransitionComponent={Transition}
					fullWidth
					maxWidth="sm"
					PaperProps={{sx: {borderRadius: 3}}}
				>
					<DialogTitle fontWeight={700}>Add New Sub-Category</DialogTitle>
					<DialogContent dividers sx={{pt: 1.5}}>
						<DialogContentText mb={3}>
							Enter a name or pick one from the list.
						</DialogContentText>

						<Autocomplete
							value={value}
							onChange={(e, nv) =>
								setValue(
									typeof nv === "string"
										? {subCategory: nv}
										: nv?.inputValue
											? {subCategory: nv.inputValue}
											: nv
								)
							}
							filterOptions={(opts, p) => {
								const filtered = filter(opts, p);
								const {inputValue} = p;
								if (
									inputValue !== "" &&
									!opts.some((o) => o.subCategory.toLowerCase() === inputValue.toLowerCase())
								) {
									filtered.push({inputValue, subCategory: `Add "${inputValue}"`});
								}
								return filtered;
							}}
							options={subCategoryList}
							getOptionLabel={(o) =>
								typeof o === "string" ? o : o.inputValue ? o.inputValue : o.subCategory
							}
							selectOnFocus
							clearOnBlur
							handleHomeEndKeys
							freeSolo
							renderOption={(props, option) => (
								<Box component="li" {...props}>
									{option.subCategory}
								</Box>
							)}
							renderInput={(params) => <TextField {...params} label="Sub-Category Name" fullWidth/>}
							onKeyDown={(e) => e.key === "Enter" && handleAdd()}
						/>
					</DialogContent>
					<DialogActions sx={{px: 3, py: 2}}>
						<Button color="secondary" onClick={() => setDialogOpen(false)}>Cancel</Button>
						<Button
							variant="contained"
							onClick={handleAdd}
							disabled={!value || !value.subCategory}
							onClick={handleAdd}
							disabled={!value || !value.subCategory}
							color="primary"
							sx={{
								color: '#000',
								borderRadius: 2,
								textTransform: "none",
								fontWeight: 600,
							}}
						>
							Add Sub-Category
						</Button>
					</DialogActions>
				</Dialog>
				<Snackbar
					open={snack.open}
					autoHideDuration={4000}
					onClose={handleSnackClose}
					anchorOrigin={{vertical: "bottom", horizontal: "center"}}
				>
					<Alert
						onClose={handleSnackClose}
						severity={snack.severity}
						sx={{width: "100%"}}
					>
						{snack.msg}
					</Alert>
				</Snackbar>
				<Backdrop open={loading} sx={{color: "#fff", zIndex: theme.zIndex.drawer + 1}}>
					<CircularProgress color="inherit"/>
				</Backdrop>
			</ThemeProvider>
		</Box>
	);
}
